import { useMemo, useState } from "react";
import { db, storage, auth } from "../../firebaseConfig";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import toast, { Toaster } from "react-hot-toast";
const WORK_OPTIONS = [
  {
    id: "mustard-procurement",
    title: "Mustard Procurement Work",
    districts: ["Banaskantha", "Vav-Tharad", "Patan"],
    commodity: "Mustard",
    deadline: "Open",
    description:
      "Apply for mustard procurement operations under SLA framework for allotted district and APMC coverage.",
  },
  {
    id: "chana-procurement",
    title: "Chana Procurement Work",
    districts: ["Banaskantha", "Vav-Tharad", "Patan"],
    commodity: "Chana",
    deadline: "Open",
    description:
      "Apply for chana procurement work with financial, license, certificate and compliance based evaluation.",
  },
];

const DOCUMENT_FIELDS = [
  { key: "registrationCertificate", label: "Registration Certificate", required: true },
  { key: "apmcLicense", label: "APMC License", required: true },
  { key: "turnoverProof", label: "Turnover Proof (Last 3 Years)", required: true },
  { key: "profitProof", label: "Profit Proof (Last 3 Years)", required: true },
  { key: "experienceProof", label: "Experience Certificate / Work Orders", required: true },
  { key: "complianceDocs", label: "PAN / GST / Compliance Documents", required: true },
  { key: "authorizedIdProof", label: "Authorized Signatory ID Proof", required: true },
  { key: "otherDocs", label: "Other Supporting Documents", required: false, multiple: true },
];

const initialForm = {
  selectedWorkId: "mustard-procurement",
  applicantType: "Cooperative Society",
  organizationName: "",
  district: "Banaskantha",
  apmcTaluka: "",
  commodity: "Mustard",
  authorizedPerson: "",
  mobile: "",
  email: "",
  turnoverYear1: "",
  turnoverYear2: "",
  turnoverYear3: "",
  profitYear1: "",
  profitYear2: "",
  profitYear3: "",
  experienceYears: "",
  apmcLicenseNumber: "",
  registrationDetails: "",
  panNumber: "",
  gstNumber: "",
  officeAddress: "",
};

const initialFiles = {
  registrationCertificate: null,
  apmcLicense: null,
  turnoverProof: null,
  profitProof: null,
  experienceProof: null,
  complianceDocs: null,
  authorizedIdProof: null,
  otherDocs: [],
};

function safeNumber(value) {
  const num = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(num) ? num : 0;
}

function calculateTurnoverScore(totalTurnover) {
  if (totalTurnover >= 100000000) return 20;
  if (totalTurnover >= 50000000) return 16;
  if (totalTurnover >= 20000000) return 12;
  if (totalTurnover > 0) return 8;
  return 0;
}

function calculateProfitScore(totalProfit) {
  if (totalProfit >= 10000000) return 10;
  if (totalProfit >= 5000000) return 8;
  if (totalProfit >= 1000000) return 5;
  if (totalProfit > 0) return 3;
  return 0;
}

function calculateExperienceScore(years) {
  const value = safeNumber(years);
  if (value >= 10) return 20;
  if (value >= 7) return 16;
  if (value >= 5) return 12;
  if (value >= 3) return 8;
  if (value > 0) return 4;
  return 0;
}

function calculateLicenseScore(apmcLicenseNumber, apmcLicenseFile) {
  return apmcLicenseNumber && apmcLicenseFile ? 15 : 0;
}

function calculateCertificateScore(registrationDetails, registrationCertificateFile) {
  return registrationDetails && registrationCertificateFile ? 15 : 0;
}

function calculateComplianceScore(files) {
  const requiredCompliance = [
    files.turnoverProof,
    files.profitProof,
    files.experienceProof,
    files.complianceDocs,
    files.authorizedIdProof,
  ].filter(Boolean).length;

  if (requiredCompliance === 5) return 20;
  if (requiredCompliance === 4) return 15;
  if (requiredCompliance === 3) return 10;
  if (requiredCompliance > 0) return 5;
  return 0;
}

function calculateScore(form, files) {
  const totalTurnover =
    safeNumber(form.turnoverYear1) +
    safeNumber(form.turnoverYear2) +
    safeNumber(form.turnoverYear3);

  const totalProfit =
    safeNumber(form.profitYear1) +
    safeNumber(form.profitYear2) +
    safeNumber(form.profitYear3);

  const breakdown = {
    turnover: calculateTurnoverScore(totalTurnover),
    profit: calculateProfitScore(totalProfit),
    experience: calculateExperienceScore(form.experienceYears),
    apmcLicense: calculateLicenseScore(form.apmcLicenseNumber, files.apmcLicense),
    certificates: calculateCertificateScore(form.registrationDetails, files.registrationCertificate),
    compliance: calculateComplianceScore(files),
  };

  const total = Object.values(breakdown).reduce((sum, value) => sum + value, 0);

  return {
    total,
    breakdown,
    financialSummary: {
      totalTurnover,
      totalProfit,
      experienceYears: safeNumber(form.experienceYears),
    },
  };
}

function validateForm(form, files) {
  const errors = {};

  if (!form.organizationName.trim()) errors.organizationName = "Organization name is required.";
  if (!form.apmcTaluka.trim()) errors.apmcTaluka = "APMC / Taluka is required.";
  if (!form.authorizedPerson.trim()) errors.authorizedPerson = "Authorized person is required.";
  if (!form.mobile.trim()) errors.mobile = "Mobile number is required.";
  if (!/^\d{10}$/.test(form.mobile.trim())) {
    errors.mobile = "Enter a valid 10 digit mobile number.";
  }
  if (!form.email.trim()) errors.email = "Email ID is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  if (!form.apmcLicenseNumber.trim()) {
    errors.apmcLicenseNumber = "APMC license number is required.";
  }
  if (!form.registrationDetails.trim()) {
    errors.registrationDetails = "Registration details are required.";
  }
  if (!form.officeAddress.trim()) errors.officeAddress = "Office address is required.";

  DOCUMENT_FIELDS.filter((doc) => doc.required).forEach((doc) => {
    if (!files[doc.key]) errors[doc.key] = `${doc.label} is required.`;
  });

  return errors;
}

async function uploadSingleFile(file, folderPath) {
  if (!file) return null;

  const fileName = `${Date.now()}-${file.name}`;
  const storageRef = ref(storage, `${folderPath}/${fileName}`);
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  return {
    name: file.name,
    size: file.size,
    type: file.type,
    url: downloadURL,
    path: storageRef.fullPath,
  };
}

async function uploadAllDocuments(files, applicationId) {
  const basePath = `procurement-applications/${applicationId}`;

  const uploaded = {
    registrationCertificate: await uploadSingleFile(
      files.registrationCertificate,
      `${basePath}/registration-certificate`
    ),
    apmcLicense: await uploadSingleFile(files.apmcLicense, `${basePath}/apmc-license`),
    turnoverProof: await uploadSingleFile(files.turnoverProof, `${basePath}/turnover-proof`),
    profitProof: await uploadSingleFile(files.profitProof, `${basePath}/profit-proof`),
    experienceProof: await uploadSingleFile(files.experienceProof, `${basePath}/experience-proof`),
    complianceDocs: await uploadSingleFile(files.complianceDocs, `${basePath}/compliance-docs`),
    authorizedIdProof: await uploadSingleFile(
      files.authorizedIdProof,
      `${basePath}/authorized-id-proof`
    ),
    otherDocs: [],
  };

  if (files.otherDocs && files.otherDocs.length > 0) {
    uploaded.otherDocs = await Promise.all(
      files.otherDocs.map((file) => uploadSingleFile(file, `${basePath}/other-docs`))
    );
  }

  return uploaded;
}

export default function ProcurementApplyPage() {
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState(initialFiles);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitType, setSubmitType] = useState("submit");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const scoreData = useMemo(() => calculateScore(form, files), [form, files]);

  const selectedWork =
    WORK_OPTIONS.find((item) => item.id === form.selectedWorkId) || WORK_OPTIONS[0];

  const checklist = [
    "Organization / Society / FPO Registration Details",
    "APMC License / Market Related Authorization",
    "Last 3 Years Turnover",
    "Last 3 Years Profit Details",
    "3 Years Experience in Procurement / Agri Operations",
    "Required Certificates and Compliance Documents",
    "Authorized Signatory Details",
    "All Required Documents Upload",
  ];

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = (field, event, multiple = false) => {
    const selectedFiles = Array.from(event.target.files || []);
    setFiles((prev) => ({
      ...prev,
      [field]: multiple ? selectedFiles : selectedFiles[0] || null,
    }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const renderInputError = (field) =>
    errors[field] ? (
      <p className="mt-2 text-xs font-medium text-red-600">{errors[field]}</p>
    ) : null;

  const preparePayload = (uploadedDocuments, status) => ({
    ...form,
    selectedWork,
    applicationStatus: status,
    score: scoreData.total,
    scoreBreakdown: scoreData.breakdown,
    financialSummary: scoreData.financialSummary,
    uploadedDocuments,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    portalType: "procurement-work-application",
  });

  const resetForm = () => {
    setForm(initialForm);
    setFiles(initialFiles);
    setErrors({});
  };

  const handleSave = async (mode = "submit") => {
    setSubmitType(mode);
    setSuccessMessage("");
    setErrorMessage("");

    if (mode === "submit") {
      const validationErrors = validateForm(form, files);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
    }

    try {
      setLoading(true);

      const applicationId = String(Date.now());
      const uploadedDocuments = await uploadAllDocuments(files, applicationId);

      const payload = preparePayload(
        uploadedDocuments,
        mode === "submit" ? "submitted" : "draft"
      );

      await addDoc(collection(db, "procurement_applications"), payload);

      setSuccessMessage(
        mode === "submit"
          ? "Application submitted successfully."
          : "Draft saved successfully."
      );

      if (mode === "submit") {
        resetForm();
      }
    } catch (error) {
      console.error("Error saving application:", error);
      setErrorMessage("Something went wrong while saving the application.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <section className="relative overflow-hidden border-b bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-10 left-10 h-40 w-40 rounded-full bg-white blur-3xl" />
          <div className="absolute right-0 top-10 h-52 w-52 rounded-full bg-yellow-200 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 py-14 lg:px-10">
          <div className="grid gap-8 lg:grid-cols-[1.4fr_0.9fr] lg:items-center">
            <div>
              <div className="mb-4 inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-1 text-sm font-medium backdrop-blur">
                SLA Procurement Portal
              </div>

              <h1 className="max-w-3xl text-3xl font-bold leading-tight md:text-5xl">
                Apply for Procurement Work
              </h1>

              <p className="mt-4 max-w-2xl text-base text-emerald-50 md:text-lg">
                Cooperative Societies, FPOs and eligible procurement partners can
                submit applications for allotted procurement work through this portal.
                Fill organization details, upload all required documents and get
                auto-evaluated on defined scoring rules.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="rounded-2xl bg-white px-6 py-3 font-semibold text-emerald-700 shadow-lg transition hover:-translate-y-0.5"
                >
                  Start Application
                </button>
                <button
                  type="button"
                  className="rounded-2xl border border-white/30 bg-white/10 px-6 py-3 font-semibold text-white backdrop-blur transition hover:bg-white/20"
                >
                  View Guidelines
                </button>
              </div>
            </div>

            <div className="rounded-3xl border border-white/20 bg-white/10 p-6 shadow-2xl backdrop-blur-xl">
              <h2 className="text-xl font-semibold">Quick Overview</h2>
              <div className="mt-5 space-y-4 text-sm">
                <div className="rounded-2xl bg-white/10 p-4">
                  <div className="text-emerald-100">Eligible Applicants</div>
                  <div className="mt-1 text-base font-semibold text-white">
                    Cooperative Society / FPO / Procurement Agency
                  </div>
                </div>

                <div className="rounded-2xl bg-white/10 p-4">
                  <div className="text-emerald-100">Current Work Areas</div>
                  <div className="mt-1 text-base font-semibold text-white">
                    Banaskantha, Vav-Tharad, Patan
                  </div>
                </div>

                <div className="rounded-2xl bg-white/10 p-4">
                  <div className="text-emerald-100">Commodities</div>
                  <div className="mt-1 text-base font-semibold text-white">
                    Mustard & Chana
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        {successMessage && (
          <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-700">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-medium text-red-700">
            {errorMessage}
          </div>
        )}

        <div className="grid gap-8 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-8">
            <section className="rounded-3xl border bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Available Procurement Work</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Select the work you want to apply for based on your district,
                    APMC reach and operational eligibility.
                  </p>
                </div>

                <div className="rounded-2xl bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
                  Live Application Window
                </div>
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                {WORK_OPTIONS.map((card) => {
                  const active = form.selectedWorkId === card.id;

                  return (
                    <button
                      type="button"
                      key={card.id}
                      onClick={() => {
                        handleInputChange("selectedWorkId", card.id);
                        handleInputChange("commodity", card.commodity);
                      }}
                      className={`rounded-3xl border p-5 text-left transition hover:-translate-y-1 hover:shadow-lg ${
                        active
                          ? "border-emerald-500 bg-emerald-50 shadow-md"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-bold">{card.title}</h3>
                          <p className="mt-2 text-sm text-slate-600">{card.description}</p>
                        </div>

                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                          {card.deadline}
                        </span>
                      </div>

                      <div className="mt-5 space-y-2 text-sm text-slate-700">
                        <div>
                          <span className="font-semibold">Commodity:</span> {card.commodity}
                        </div>
                        <div>
                          <span className="font-semibold">Districts:</span>{" "}
                          {card.districts.join(" • ")}
                        </div>
                      </div>

                      <div
                        className={`mt-5 inline-flex rounded-2xl px-4 py-2 text-sm font-semibold ${
                          active ? "bg-emerald-600 text-white" : "bg-slate-900 text-white"
                        }`}
                      >
                        {active ? "Selected" : "Select Work"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="rounded-3xl border bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-bold">Procurement Application Form</h2>
              <p className="mt-1 text-sm text-slate-600">
                Fill all details carefully. Your application will be automatically
                evaluated based on financial, license and document scoring parameters.
              </p>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold">Applicant Type</label>
                  <select
                    value={form.applicantType}
                    onChange={(e) => handleInputChange("applicantType", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-emerald-500"
                  >
                    <option>Cooperative Society</option>
                    <option>FPO</option>
                    <option>Procurement Agency</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Organization Name</label>
                  <input
                    value={form.organizationName}
                    onChange={(e) => handleInputChange("organizationName", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter organization name"
                  />
                  {renderInputError("organizationName")}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">District Applied For</label>
                  <select
                    value={form.district}
                    onChange={(e) => handleInputChange("district", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                  >
                    <option>Banaskantha</option>
                    <option>Vav-Tharad</option>
                    <option>Patan</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">APMC / Taluka</label>
                  <input
                    value={form.apmcTaluka}
                    onChange={(e) => handleInputChange("apmcTaluka", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter APMC / Taluka name"
                  />
                  {renderInputError("apmcTaluka")}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Commodity</label>
                  <select
                    value={form.commodity}
                    onChange={(e) => handleInputChange("commodity", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                  >
                    <option>Mustard</option>
                    <option>Chana</option>
                    <option>Mustard + Chana</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Authorized Person</label>
                  <input
                    value={form.authorizedPerson}
                    onChange={(e) => handleInputChange("authorizedPerson", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter authorized person name"
                  />
                  {renderInputError("authorizedPerson")}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Mobile Number</label>
                  <input
                    value={form.mobile}
                    onChange={(e) => handleInputChange("mobile", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter mobile number"
                  />
                  {renderInputError("mobile")}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Email ID</label>
                  <input
                    value={form.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter email address"
                  />
                  {renderInputError("email")}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Turnover Year 1 (₹)</label>
                  <input
                    value={form.turnoverYear1}
                    onChange={(e) => handleInputChange("turnoverYear1", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter year 1 turnover"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Turnover Year 2 (₹)</label>
                  <input
                    value={form.turnoverYear2}
                    onChange={(e) => handleInputChange("turnoverYear2", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter year 2 turnover"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Turnover Year 3 (₹)</label>
                  <input
                    value={form.turnoverYear3}
                    onChange={(e) => handleInputChange("turnoverYear3", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter year 3 turnover"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Profit Year 1 (₹)</label>
                  <input
                    value={form.profitYear1}
                    onChange={(e) => handleInputChange("profitYear1", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter year 1 profit"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Profit Year 2 (₹)</label>
                  <input
                    value={form.profitYear2}
                    onChange={(e) => handleInputChange("profitYear2", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter year 2 profit"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Profit Year 3 (₹)</label>
                  <input
                    value={form.profitYear3}
                    onChange={(e) => handleInputChange("profitYear3", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter year 3 profit"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Procurement Experience (Years)</label>
                  <input
                    value={form.experienceYears}
                    onChange={(e) => handleInputChange("experienceYears", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter procurement experience"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">APMC License Number</label>
                  <input
                    value={form.apmcLicenseNumber}
                    onChange={(e) => handleInputChange("apmcLicenseNumber", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter APMC license number"
                  />
                  {renderInputError("apmcLicenseNumber")}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">Registration / Certificate Details</label>
                  <input
                    value={form.registrationDetails}
                    onChange={(e) => handleInputChange("registrationDetails", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter certificate or registration details"
                  />
                  {renderInputError("registrationDetails")}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">PAN Number</label>
                  <input
                    value={form.panNumber}
                    onChange={(e) => handleInputChange("panNumber", e.target.value.toUpperCase())}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter PAN number"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">GST Number</label>
                  <input
                    value={form.gstNumber}
                    onChange={(e) => handleInputChange("gstNumber", e.target.value.toUpperCase())}
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter GST number"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="mb-2 block text-sm font-semibold">Office Address</label>
                <textarea
                  value={form.officeAddress}
                  onChange={(e) => handleInputChange("officeAddress", e.target.value)}
                  className="min-h-[110px] w-full rounded-2xl border border-slate-300 px-4 py-3 focus:border-emerald-500 focus:outline-none"
                  placeholder="Enter full office address"
                />
                {renderInputError("officeAddress")}
              </div>

              <div className="mt-5">
                <label className="mb-2 block text-sm font-semibold">Upload All Required Documents</label>
                <div className="grid gap-4 md:grid-cols-2">
                  {DOCUMENT_FIELDS.map((doc) => (
                    <div key={doc.key}>
                      <label className="mb-2 block text-sm font-medium">
                        {doc.label} {doc.required ? <span className="text-red-500">*</span> : null}
                      </label>
                      <input
                        type="file"
                        multiple={doc.multiple}
                        onChange={(e) => handleFileChange(doc.key, e, !!doc.multiple)}
                        className="w-full rounded-2xl border border-slate-300 px-4 py-3"
                      />
                      <p className="mt-2 text-xs text-slate-500">
                        {doc.multiple
                          ? `${files[doc.key]?.length || 0} files selected`
                          : files[doc.key]?.name || "No file selected"}
                      </p>
                      {renderInputError(doc.key)}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => handleSave("submit")}
                  className="rounded-2xl bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading && submitType === "submit" ? "Submitting..." : "Submit Application"}
                </button>

                <button
                  type="button"
                  disabled={loading}
                  onClick={() => handleSave("draft")}
                  className="rounded-2xl border border-slate-300 px-6 py-3 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading && submitType === "draft" ? "Saving Draft..." : "Save as Draft"}
                </button>
              </div>
            </section>
          </div>

          <div className="space-y-8">
            <section className="rounded-3xl border bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold">Eligibility Checklist</h2>
              <div className="mt-4 space-y-3">
                {checklist.map((item) => (
                  <div key={item} className="flex gap-3 rounded-2xl bg-slate-50 p-3">
                    <div className="mt-0.5 h-5 w-5 rounded-full bg-emerald-100 text-center text-xs font-bold leading-5 text-emerald-700">
                      ✓
                    </div>
                    <p className="text-sm text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-3xl border bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold">Live Auto Evaluation</h2>
              <p className="mt-2 text-sm text-slate-600">
                Score updates automatically as the applicant fills details and uploads required documents.
              </p>

              <div className="mt-5 rounded-3xl bg-slate-900 p-5 text-white">
                <div className="text-sm text-slate-300">Current Score</div>
                <div className="mt-2 text-4xl font-bold">{scoreData.total}/100</div>
              </div>

              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                  <span>Turnover Score</span>
                  <span className="font-bold">{scoreData.breakdown.turnover} / 20</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                  <span>Profit Score</span>
                  <span className="font-bold">{scoreData.breakdown.profit} / 10</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                  <span>Experience Score</span>
                  <span className="font-bold">{scoreData.breakdown.experience} / 20</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                  <span>APMC License Score</span>
                  <span className="font-bold">{scoreData.breakdown.apmcLicense} / 15</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                  <span>Certificate / Registration Score</span>
                  <span className="font-bold">{scoreData.breakdown.certificates} / 15</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                  <span>Compliance Documents Score</span>
                  <span className="font-bold">{scoreData.breakdown.compliance} / 20</span>
                </div>
              </div>
            </section>

            <section className="rounded-3xl border bg-slate-900 p-6 text-white shadow-sm">
              <h2 className="text-xl font-bold">Need Assistance?</h2>
              <p className="mt-2 text-sm text-slate-300">
                For application support, document clarification, scoring help or district / APMC mapping assistance, contact the procurement helpdesk.
              </p>
              <div className="mt-5 space-y-3 text-sm">
                <div className="rounded-2xl bg-white/10 p-4">
                  Helpdesk Email: procurement@shineagri.org
                </div>
                <div className="rounded-2xl bg-white/10 p-4">
                  Support Number: +91 8490884020
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}